import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, Briefcase, Users, Building2, ChevronRight, ChevronDown, Plus, ChevronsLeft, ChevronsRight, Crown, ArrowLeft, MapPin, FileText, UserPlus, TrendingUp, HelpCircle } from 'lucide-react';

import Button from '../../../components/ui/Button';
import Tooltip from '../../../components/ui/Tooltip';
import { useRoleAccess } from '../../../hooks/useRoleAccess';
import { hasFeatureAccess } from '../../../utils/roleValidation';
import { useMobileResponsive, useSwipeGesture } from '../../../hooks/useMobileResponsive';

const CompanyDashboardSidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useMobileResponsive();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Add this block - Extract companyId from URL
  const companyId = location.pathname.split('/')[2]; // Extracts companyId from /company-dashboard-hub/:companyId path

  // Swipe gesture handlers for mobile
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => {
      if (isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    },
    onSwipeRight: () => {
      if (isMobile && !mobileOpen) {
        setMobileOpen(true);
      }
    }
  });

  // Close mobile sidebar when route changes
  React.useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [location?.pathname]);

  const { userRole, canManageTeam } = useRoleAccess();

  const contentVariants = {
    collapsed: { opacity: 0, width: 0 },
    expanded: { opacity: 1, width: 'auto' }
  };

  const mockCompanies = [
    { id: '1', name: 'Company A', logo: 'CA', role: 'admin', unreadCount: 0 },
    { id: '2', name: 'Company B', logo: 'CB', role: 'member', unreadCount: 2 },
    { id: '3', name: 'Company C', logo: 'CC', role: 'admin', unreadCount: 0 }
  ];

  // Define menu items with required permissions
  const menuItems = [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: LayoutGrid, 
      path: 'overview',
      requiredFeature: null // Always visible
    },
    { 
      id: 'jobs', 
      label: 'Jobs', 
      icon: Briefcase, 
      path: 'jobs',
      requiredFeature: 'manage_jobs'
    },
    { 
      id: 'candidates', 
      label: 'Candidates', 
      icon: Users, 
      path: 'candidates',
      requiredFeature: 'view_candidates'
    },
    { 
      id: 'applications', 
      label: 'Applications', 
      icon: FileText, 
      path: 'applications',
      requiredFeature: 'review_applications'
    },
    { 
      id: 'team', 
      label: 'Team', 
      icon: UserPlus, 
      path: 'team',
      requiredFeature: 'manage_team'
    },
    { 
      id: 'company', 
      label: 'Company Profile', 
      icon: Building2, 
      path: 'company',
      requiredFeature: 'manage_company_profile'
    },
    { 
      id: 'locations', 
      label: 'Locations', 
      icon: MapPin, 
      path: 'locations',
      requiredFeature: 'manage_locations'
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: TrendingUp, 
      path: 'analytics',
      requiredFeature: 'view_analytics'
    },
    { 
      id: 'support', 
      label: 'Help & Support', 
      icon: HelpCircle, 
      path: '/support-ticket-management-dashboard',
      requiredFeature: null,
      isExternal: true
    }
  ];

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems?.filter(item => {
    if (!item?.requiredFeature) return true;
    return hasFeatureAccess(userRole, item?.requiredFeature);
  });

  const sidebarClasses = "fixed left-0 top-16 bottom-0 bg-background border-r border-border z-30 overflow-hidden";

  const sidebarVariants = {
    collapsed: { x: -256 },
    expanded: { x: 0 }
  };

  const renderMenuItem = (item) => {
    return (
      <li key={item?.id} className="relative">
        <div className="flex items-center">
          <NavLink
            to={item?.path}
            end={true}
            className={({ isActive }) => {
              let shouldBeActive = isActive;
              
              if (item?.expandable && isActive) {
                const currentLocation = window.location?.pathname + window.location?.search;
                const itemPathWithoutQuery = item?.path?.split('?')?.[0];
                const currentPathWithoutQuery = currentLocation?.split('?')?.[0];
                
                shouldBeActive = currentPathWithoutQuery === itemPathWithoutQuery && 
                                (!window.location?.search || window.location?.search === '');
              }
              
              return `flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-1 ${
                collapsed ? 'justify-center' : ''
              } ${
                shouldBeActive
                  ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
              }`;
            }}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  variants={contentVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  className="truncate flex-1"
                >
                  {item?.label}
                </motion.span>
              )}
            </AnimatePresence>
            
            {/* Badge */}
            {item?.badge && !collapsed && (
              <motion.span
                variants={contentVariants}
                initial="collapsed"
                animate="expanded"
                exit="collapsed"
                className="bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full font-medium"
              >
                {item?.badge}
              </motion.span>
            )}
          </NavLink>

          {/* Expandable toggle */}
          {item?.expandable && !collapsed && (
            <button
              onClick={item?.onToggle}
              className="p-1 rounded hover:bg-accent/10 transition-colors ml-1"
            >
              {item?.expanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          )}
        </div>
        {/* Sub-items */}
        <AnimatePresence>
          {item?.expandable && item?.expanded && !collapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="ml-6 mt-1 space-y-1"
            >
              {item?.subItems?.map(subItem => (
                <NavLink
                  key={subItem?.id}
                  to={subItem?.path}
                  end={true}
                  className={({ isActive }) => {
                    const currentLocation = window.location?.pathname + window.location?.search;
                    const isSubItemActive = currentLocation === subItem?.path;
                    
                    return `block px-3 py-1.5 text-sm rounded transition-colors ${
                      isSubItemActive
                        ? 'bg-accent/5 text-accent font-medium' : 'text-muted-foreground hover:bg-accent/5 hover:text-accent'
                    }`;
                  }}
                >
                  {subItem?.label}
                </NavLink>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        {collapsed && (
          <Tooltip content={item?.label} side="right">
            <div className="absolute inset-0" />
          </Tooltip>
        )}
      </li>
    );
  };

  return (
    <div>
      {/* Mobile Backdrop */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={onToggle}
            aria-label="Close sidebar"
          />
        )}
      </AnimatePresence>
      {/* Sidebar */}
      <motion.aside
        {...swipeHandlers}
        initial={false}
        animate={collapsed ? "collapsed" : "expanded"}
        variants={sidebarVariants}
        className={`fixed left-0 top-16 bottom-0 bg-background border-r border-border z-30 overflow-y-auto ${
          isMobile ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') : ''
        } transition-transform duration-300`}
      >
        <div className="h-full flex flex-col">
          {/* Toggle Button - Desktop Only */}
          <div className="hidden lg:flex items-center justify-end p-3 border-b border-border">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronsRight className="w-4 h-4" />
              ) : (
                <ChevronsLeft className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <ul className="space-y-1">
              {filteredMenuItems?.map((item) => (
                <li key={item?.id}>
                  {renderMenuItem(item)}
                </li>
              ))}
            </ul>
          </nav>

          {/* Multi-Company Navigation Section */}
          <div className="border-t border-border p-4 space-y-2">
            {/* Job Seeker Dashboard Switch */}
            <div className="relative">
              <button
                onClick={() => navigate('/dashboard')}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  collapsed ? 'justify-center' : ''
                } text-muted-foreground hover:bg-accent/10 hover:text-accent`}
              >
                <ArrowLeft className="w-5 h-5 flex-shrink-0" />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      variants={contentVariants}
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      className="truncate flex-1 text-left"
                    >
                      Job Seeker Dashboard
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              {collapsed && (
                <Tooltip content="Switch to Job Seeker Dashboard" side="right">
                  <div className="absolute inset-0" />
                </Tooltip>
              )}
            </div>

            {/* Company Switcher */}
            <div className="relative">
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  collapsed ? 'justify-center' : ''
                } text-muted-foreground hover:bg-accent/10 hover:text-accent`}
              >
                <Building2 className="w-5 h-5 flex-shrink-0" />
                <AnimatePresence>
                  {!collapsed && (
                    <>
                      <motion.span
                        variants={contentVariants}
                        initial="collapsed"
                        animate="expanded"
                        exit="collapsed"
                        className="truncate flex-1 text-left"
                      >
                        Switch Company
                      </motion.span>
                      <motion.div
                        variants={contentVariants}
                        initial="collapsed"
                        animate="expanded"
                        exit="collapsed"
                      >
                        {mobileOpen ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </button>

              {collapsed && (
                <Tooltip content="Switch Company" side="right">
                  <div className="absolute inset-0" />
                </Tooltip>
              )}
            </div>

            {/* Company Dropdown */}
            <AnimatePresence>
              {mobileOpen && !collapsed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="ml-6 space-y-2 max-h-48 overflow-y-auto"
                >
                  {mockCompanies?.filter(company => company?.id !== companyId)?.map(company => (
                    <button
                      key={company?.id}
                      onClick={() => navigate(`/company-dashboard-hub/${company?.id}`)}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors text-muted-foreground hover:bg-accent/5 hover:text-accent"
                    >
                      <div className="w-6 h-6 bg-accent text-accent-foreground rounded text-xs font-semibold flex items-center justify-center">
                        {company?.logo}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium truncate">{company?.name}</div>
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                          {company?.role === 'admin' ? <Crown className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                          <span className="capitalize">{company?.role}</span>
                        </div>
                      </div>
                      {company?.unreadCount > 0 && (
                        <div className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                          {company?.unreadCount}
                        </div>
                      )}
                    </button>
                  ))}

                  {/* Create Company Button */}
                  <button
                    onClick={() => navigate('/multi-company-navigation-sidebar?action=create')}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors text-muted-foreground hover:bg-accent/5 hover:text-accent border-t border-border pt-3 mt-3"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Create New Company</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>
    </div>
  );
};

export default CompanyDashboardSidebar;